import React, { useEffect } from 'react';
import { Controller, UseFormSetValue } from 'react-hook-form';
import { CollaboratorFormContext } from "@/features/Collaborators/Edit/Contexts/CollaboratorFormContext";
import { collaboratorSchemaHelpers } from '@/features/Collaborators/Models/collaborator';
import { CollaboratorCombobox, DepartmentCombobox, RequiredIndicator } from '@/components/shared';

interface ResponsibilitiesFieldsetProps {
    context: CollaboratorFormContext;
    control: any;
    setValue: UseFormSetValue<any>;
    watch?: any;
    errors?: Record<string, any>;
    isSubmitting?: boolean;
    layout?: 'full' | 'compact';
}

const ResponsibilitiesFieldset: React.FC<ResponsibilitiesFieldsetProps> = ({
    context,
    control,
    setValue,
    watch,
    errors = {},
    isSubmitting = false,
    layout = 'full'
}) => {
    // Helper para verificar se campo deve aparecer usando helper global
    const shouldShow = (fieldName: string) => collaboratorSchemaHelpers.shouldShowField(fieldName, context);

    // Helper global para verificar se campos são obrigatórios
    const isRequired = (fieldName: string) => collaboratorSchemaHelpers.isFieldRequired(fieldName, context);



    // Verificar se algum campo deve ser mostrado
    const hasVisibleFields = shouldShow('departmentId') || shouldShow('responsibleId') || shouldShow('clientResponsible');

    if (!hasVisibleFields) {
        return null;
    }

    return (
        <fieldset className="border p-4 rounded-md">
            <legend className="text-sm font-medium px-1 text-gray-400">Responsáveis</legend>
            <div className="space-y-4 pt-2">
                {/* Departamento */}
                {shouldShow('departmentId') && (
                    <div>
                        <label htmlFor="department" className="block text-sm font-medium text-gray-700">
                            Departamento
                            {isRequired('departmentId') && <RequiredIndicator />}
                        </label>
                        <Controller
                            name="departmentId"
                            control={control}
                            render={({ field }) => (
                                <DepartmentCombobox
                                    selectedId={field.value}
                                    onChange={(department) => {
                                        field.onChange(department?.id || null);
                                        // Atualizar departmentName quando disponível
                                        if (department && setValue) {
                                            setValue('departmentName', department.name);
                                        }
                                    }}
                                    disabled={isSubmitting}
                                    data-testid="department-combobox"
                                />
                            )}
                        />
                        {errors.departmentId && (
                            <p className="mt-1 text-sm text-red-600">{errors.departmentId.message}</p>
                        )}

                    </div>
                )}

                <div className={`grid gap-4 ${layout === 'compact' ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
                    {/* Responsável Raro */}
                    {shouldShow('responsibleId') && (
                        <div>
                            <label htmlFor="responsibleRaro" className="block text-sm font-medium text-gray-700">
                                Responsável Raro
                                {isRequired('responsibleId') && <RequiredIndicator />}
                            </label>
                            <Controller
                                name="responsibleId"
                                control={control}
                                render={({ field }) => (
                                    <CollaboratorCombobox
                                        selectedId={field.value}
                                        onChange={(collaborator) => {
                                            field.onChange(collaborator?.id || null);
                                            // Atualizar responsibleName quando disponível
                                            if (collaborator && setValue) {
                                                setValue('responsibleName', collaborator.name);
                                            }
                                        }}
                                        disabled={isSubmitting}
                                        data-testid="responsible-raro-combobox"
                                    />
                                )}
                            />
                            {errors.responsibleId && (
                                <p className="mt-1 text-sm text-red-600">{errors.responsibleId.message}</p>
                            )}

                        </div>
                    )}

                    {/* Responsável no Cliente */}
                    {shouldShow('clientResponsible') && (
                        <div>
                            <label htmlFor="clientResponsible" className="block text-sm font-medium text-gray-700">
                                Responsável no Cliente
                                {isRequired('clientResponsible') && <RequiredIndicator />}
                            </label>
                            <Controller
                                name="clientResponsible"
                                control={control}
                                render={({ field }) => (
                                    <input
                                        {...field}
                                        id="clientResponsible"
                                        className="input-primary w-full mt-1"
                                        disabled={isSubmitting}
                                        data-testid="client-responsible-input"
                                    />
                                )}
                            />
                            {errors.clientResponsible && (
                                <p className="mt-1 text-sm text-red-600">{errors.clientResponsible.message}</p>
                            )}

                        </div>
                    )}
                </div>
            </div>
        </fieldset>
    );
};

export default ResponsibilitiesFieldset; 